import React, { useRef, useState, useEffect } from "react";

// Finch-style circular duration picker — drag the dot around the ring to set
// minutes, snapping to 5-minute increments from `min` up to `max` (200).
// Replaces the old plain number-field inputs for the custom timer's
// Duration/Break settings.
export function DurationRingPicker({
  value, onChange, min = 5, max = 200, step = 5, size = 108, strokeWidth = 11,
  color = "var(--c-primary-deep)", trackColor = "#f0e0ec", label, zeroLabel = null, disabled = false,
}) {
  const svgRef = useRef(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  // Dragging tracked as state (not just a ref) so the window-level listener
  // effect below attaches/detaches at the right time — see the useEffect.
  const [dragging, setDragging] = useState(false);
  // While dragging, the dot/arc follow the pointer's raw continuous angle —
  // decoupled from the snapped `value` — so the motion feels fluid instead of
  // hopping between 5-minute steps. The committed value (and the number shown)
  // still snaps; only the visual position tracks the finger continuously.
  const [liveAngle, setLiveAngle] = useState(null);
  const R = (size - strokeWidth) / 2;
  // The drag dot sits ON the ring (radius R from center) but has its own
  // radius on top of that, so it reaches slightly past R — past the edge of
  // a viewBox sized exactly to `size`, clipping it flush against the SVG
  // boundary. Pad the actual canvas by the dot's radius (+ a small buffer for
  // its stroke) so it always has room to render in full; the ring itself
  // still measures exactly `size` px, just recentered within the bigger canvas.
  const dotR = strokeWidth * 0.72 + 2.5;
  const pad = dotR + 2;
  const canvas = size + pad * 2;
  const cx = canvas / 2, cy = canvas / 2;
  const CIRC = 2 * Math.PI * R;
  const clamped = Math.min(max, Math.max(min, value));
  const valueFrac = (clamped - min) / (max - min);
  const angle = liveAngle != null ? liveAngle : valueFrac * 2 * Math.PI; // 0 at top, clockwise
  const frac = angle / (2 * Math.PI);
  const handleX = cx + R * Math.sin(angle);
  const handleY = cy - R * Math.cos(angle);

  const angleFromEvent = (clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    const dx = clientX - (rect.left + rect.width / 2);
    const dy = clientY - (rect.top + rect.height / 2);
    let theta = Math.atan2(dx, -dy);
    if (theta < 0) theta += 2 * Math.PI;
    return theta;
  };
  const valueFromAngle = (theta) => {
    const f = theta / (2 * Math.PI);
    const raw = min + f * (max - min);
    const snapped = Math.round(raw / step) * step;
    return Math.min(max, Math.max(min, snapped));
  };

  const applyFromEvent = (clientX, clientY) => {
    const theta = angleFromEvent(clientX, clientY);
    setLiveAngle(theta);
    const next = valueFromAngle(theta);
    if (next !== valueRef.current) { valueRef.current = next; onChange(next); }
  };

  const onPointerDown = (e) => {
    if (disabled) return;
    e.preventDefault();
    applyFromEvent(e.clientX, e.clientY);
    setDragging(true);
  };

  // Track the drag with window-level listeners rather than per-element
  // pointer capture, so movement keeps registering anywhere on the page for
  // as long as the button/finger is down, regardless of exactly where the
  // pointer drifts relative to the ring.
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => applyFromEvent(e.clientX, e.clientY);
    const onUp = () => { setDragging(false); setLiveAngle(null); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <svg ref={svgRef} width={canvas} height={canvas} viewBox={`0 0 ${canvas} ${canvas}`}
        style={{ touchAction: "none", cursor: disabled ? "default" : "grab", opacity: disabled ? 0.5 : 1 }}
        onPointerDown={onPointerDown}>
        {/* Invisible, generously-wide hit area — the visible ring is only
            `strokeWidth` (10-11px) wide, and SVG shapes with fill="none" only
            register pointer events on the painted stroke itself. A real drag
            gesture drifts off that thin band constantly, which is what made
            dragging feel like it kept "losing" the pointer mid-motion instead
            of tracking smoothly. This transparent stroke (still "painted", so
            still hit-testable) covers a much wider band around the ring. */}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="transparent" strokeWidth={Math.max(48, strokeWidth * 4)} style={{ cursor: disabled ? "default" : "grab" }} />
        <circle cx={cx} cy={cy} r={R} fill="none" stroke={trackColor} strokeWidth={strokeWidth} style={{ pointerEvents: "none" }} />
        <circle cx={cx} cy={cy} r={R} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - frac)}
          strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`} style={{ pointerEvents: "none" }} />
        <circle cx={handleX} cy={handleY} r={dotR} fill="var(--c-bg-card)" stroke={color} strokeWidth={2.5} style={{ pointerEvents: "none" }} />
        <text x={cx} y={cy - 1} textAnchor="middle" dominantBaseline="middle" fontSize={size * 0.24} fontWeight={800} fill="var(--c-text)" style={{ fontFamily: "Quicksand", pointerEvents: "none" }}>
          {clamped === 0 && zeroLabel ? "-" : clamped}
        </text>
        <text x={cx} y={cy + size * 0.19} textAnchor="middle" fontSize={size * 0.085} fontWeight={700} fill="var(--c-text-soft)" style={{ textTransform: "uppercase", letterSpacing: 1, pointerEvents: "none" }}>
          {clamped === 0 && zeroLabel ? zeroLabel : "min"}
        </text>
      </svg>
      {label && <span style={{ fontSize: 11, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</span>}
    </div>
  );
}
